import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import View360, { EquirectProjection } from '@egjs/react-view360';

import '@egjs/react-view360/css/view360.min.css';

import useGalleryScenes from '../hooks/useGalleryScenes';
import { useGallerySceneLocationSVG } from '../hooks/useGallerySceneLocationSVG';
import { PanoramaCompass } from './PanoramaCompass';
import { showPanoramaViewAtom } from '../states/planner';
import { useAtom } from 'jotai';

export const PanoramaViewer = ({ showAllTime }: { showAllTime?: boolean }) => {
  const viewerRef = useRef<View360 | null>(null);
  const [galleryScenes] = useGalleryScenes();
  const [projection, setProjection] = useState<EquirectProjection | null>(null);
  const [yaw, setYaw] = useState(0); // 👈 Track yaw
  const [showPanoramaView, setShowPanoramaView] = useAtom(showPanoramaViewAtom);

  const projections = useMemo(() => {
    if (!galleryScenes) return [];
    return galleryScenes.map(scene => new EquirectProjection({ src: scene.imageurl }));
  }, [galleryScenes]);

  useEffect(() => {
    if (projections.length > 0) {
      setProjection(projections[0]);
      if (showAllTime) {
        setShowPanoramaView(true);
      }
    }
  }, [projections, showAllTime, setShowPanoramaView]);

  const currentIndex = projections.findIndex(p => p === projection);
  const currentScene = galleryScenes?.[currentIndex];

  if (currentScene && currentIndex === 1) currentScene.svgurl = '/svg/angra.svg';
  if (currentScene && currentIndex === 0) currentScene.svgurl = '/svg/angra2.svg';

  const [locationsSvg] = useGallerySceneLocationSVG(currentScene?.svgurl);

  useLayoutEffect(() => {
    const view360 = viewerRef.current;
    if (view360 && locationsSvg?.length) {
      // view360.hotspot.refresh();
    }
  }, [locationsSvg]);

  return showPanoramaView && currentScene ? (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90">
      {!showAllTime && (
        <div
          tabIndex={0}
          role="button"
          className="absolute inset-0 cursor-default bg-black/90"
          onClick={() => setShowPanoramaView(false)}
        />
      )}
      <div className="relative h-full w-full overflow-hidden bg-black md:h-[80vh] md:max-w-7xl md:rounded-lg">
        <View360
          ref={viewerRef}
          className="h-full w-full"
          projection={projection}
          autoplay={{ delay: 5000, speed: 0.5 }}
          on={{
            viewChange: e => {
              setYaw(e.yaw); // 👈 Update yaw on view change
            },
            projectionChange: e => {
              e.target.hotspot.refresh();
            },
          }}
          hotspot={{ zoom: true }}
        >
          <div className="view360-hotspots">
            {locationsSvg?.map((loc, index) => (
              <button
                key={`hotspotlinks-${index}`}
                className="view360-hotspot flex h-8 w-8 items-center justify-center rounded-full bg-white font-bold text-black"
                data-yaw={loc.yaw}
                data-pitch={loc.pitch}
                onClick={() => setProjection(projections[loc.ref_id as unknown as number])}
              >
                {index + 10}
              </button>
            ))}
          </div>
        </View360>

        {/* 🔁 Compass overlayed above the viewer */}
        <PanoramaCompass yaw={yaw} northOffset={0} />

        <div className="absolute left-0 top-40 z-50 flex -translate-y-1/2 flex-col gap-2">
          {galleryScenes.map((scene, index) => (
            <button
              key={`scene-button-${index}`}
              onClick={() => setProjection(projections[index])}
              className="rounded-r-md bg-black bg-opacity-50 px-4 py-2 text-sm text-white transition hover:scale-105"
            >
              {scene.name ?? `Scen ${index + 1}`}
            </button>
          ))}
        </div>

        {/* Floorplan with view radar */}
        {/* Floorplan with view radar */}
        <div className="absolute right-4 top-4 z-50 h-40 w-40 rounded bg-white bg-opacity-80 p-2 sm:h-64 sm:w-64">
          <div
            className="relative h-full w-full bg-contain bg-center bg-no-repeat"
            style={{ backgroundImage: "url('/planlosning.png')" }}
          >
            {/* Camera position with direction SVG */}
            <svg
              className="absolute"
              style={{
                left: `50%`, // Set actual camera X %
                top: `50%`, // Set actual camera Y %
                transform: `translate(-50%, -50%) rotate(${yaw + 180}deg)`,
                width: '60px',
                height: '60px',
              }}
              viewBox="0 0 100 100"
            >
              {/* Triangle "view cone" */}
              <polygon
                points="50,50 20,90 80,90"
                fill="rgba(220, 38, 38, 0.6)" // Tailwind red-600 with transparency
              />
              {/* Camera dot in center */}
              <circle cx="50" cy="50" r="5" fill="rgb(220, 38, 38)" />
            </svg>
          </div>
        </div>
        {!showAllTime ? (
          <button
            onClick={() => setShowPanoramaView(false)}
            className="absolute left-1/2 top-4 -translate-x-1/2 rounded bg-black/50 px-4 py-2 text-white hover:bg-black/75"
          >
            ✕
          </button>
        ) : null}
      </div>
    </div>
  ) : null;
};
